/**
 *
 *  Time Slots panel (for mobiles)
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 20/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types"; // use directly

import { useAppState } from "../../../state";

// Components
import Button from "../../Button/Button";
import BookingFlowDayTimeSelector from "../DayTimeSelector/DayTimeSelector";
import BookingFlowWorkoutTypeSelector from "../WorkoutTypeSelector/WorkoutTypeSelector";

// Utils
import { getMoment } from "../../../utils/date";

import ArrowRightIcon from "../../../icons/ArrowRightIcon";

const BookingFlowPanel = ({
  className,
  disabled,
  isFetching,
  isFullyOpen,
  // culture,
  region,
  partnerId,
  partnerFirstName,
  bookingStep,
  bookingTime,
  min,
  max,
  availableDays,
  workoutValues,
  workout,
  // sessionType,
  partnerAvailability,
  partnerAvailabilityLoaded,
  // clearPartnerSummaryReceive,
  clearPartnerAvailabilityLoaded,
  onDateTimeSelect,
  onWorkoutChange,
  onCtaClick,
  onClose,
}) => {
  const {
    loadPartnerProfile,
    // clearPartnerSummaryReceive,
  } = useAppState();

  const dayTimePickerRef = useRef();

  useEffect(() => {
    if (partnerId) {
      loadPartnerProfile(partnerId);
    }

    return () => {
      // clearPartnerSummaryReceive();
      clearPartnerAvailabilityLoaded();
    };
  }, [partnerId]); // eslint-disable-line

  /*
   * @param {Date} dateTime
   * @param {String} dateStr - formatted string
   * @param {boolean} time - is time selected
   *
   */
  const handleDateTimeSelect = (dateTime, dateStr, time) => {
    // Pass through callback to parent
    onDateTimeSelect && onDateTimeSelect(dateTime, time);
  };

  let canBook =
    bookingStep === "CHOOSE_DAY_TIME" || // We always have date set
    (workout && bookingStep === "CHOOSE_WORKOUT_TYPE") ||
    workout;
  const isOnline = false;

  return (
    <aside
      className={`booking-flow-panel tbk-z-995 tbk-flex tbk-h-full tbk-flex-col tbk-bg-basic-white ${className}`}
    >
      <div className="booking-flow-panel-body tbk-text-main tbk-grow">
        {bookingStep === "CHOOSE_DAY_TIME" ? (
          <BookingFlowDayTimeSelector
            id="booking_flow_panel"
            disabled={disabled || isFetching}
            isFullyOpen={isFullyOpen}
            region={region}
            className={`booking-datetime-picker-input_what ${
              isOnline ? "virtual" : "in-person"
            }`}
            availableDays={availableDays}
            view="week"
            min={min}
            max={max}
            minTime="5:30"
            format="DD MMM, hh:mm A"
            headerFormat="MMMM"
            footerFormat="DD MMM"
            timeFormat="HH:mm"
            partnerAvailability={partnerAvailability}
            partnerAvailabilityLoaded={partnerAvailabilityLoaded}
            step={30}
            bookingTime={bookingTime && getMoment(bookingTime).toDate()}
            timeComponent={({ item }) => (
              <button type="button" disabled={item.disabled}>
                {item.label}
              </button>
            )}
            onDateTimeSelect={handleDateTimeSelect}
            ref={dayTimePickerRef}
          />
        ) : (
          <BookingFlowWorkoutTypeSelector
            workoutValues={workoutValues}
            workout={workout}
            onWorkoutChange={onWorkoutChange}
          />
        )}
      </div>
      <div className="tbk-absolute tbk-left-0 tbk-right-0 tbk-bottom-0 tbk-shrink-0 tbk-bg-basic-white tbk-px-2">
        <Button
          className="tbk-mb-3 tbk-w-full"
          disabled={!canBook}
          onClick={onCtaClick}
          endIcon={<ArrowRightIcon />}
        >
          {bookingStep === "CHOOSE_DAY_TIME"
            ? "Next"
            : `Book ${partnerFirstName}`}
        </Button>
      </div>
    </aside>
  );
};

BookingFlowPanel.propTypes = {
  sessionType: PropTypes.string,
  isFullyOpen: PropTypes.bool.isRequired,
  partnerId: PropTypes.number,
  partnerFirstName: PropTypes.string,
  bookingStep: PropTypes.string,
  bookingTime: PropTypes.instanceOf(Date),
  workout: PropTypes.object, //workout,
  workoutValues: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  partnerSummary: PropTypes.shape({
    firstName: PropTypes.string,
    id: PropTypes.number,
    lastName: PropTypes.string,
    phoneNumber: PropTypes.string,
    products: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })
    ),
    region: PropTypes.string,
  }),
  loadPartnerProfile: PropTypes.func.isRequired,
  partnerLoaded: PropTypes.bool,
  partnerAvailability: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.string,
    })
  ),
  partnerAvailabilityLoaded: PropTypes.bool,
  clearPartnerAvailabilityLoaded: PropTypes.func,
  onClose: PropTypes.func,
  onCtaClick: PropTypes.func,
  onDateTimeSelect: PropTypes.func.isRequired,
  onWorkoutChange: PropTypes.func.isRequired,
};

BookingFlowPanel.defaultProps = {
  bookingStep: 1,
};

export default BookingFlowPanel;
